import { toast } from "@jsq/ds/core";
import { getErrorMessage } from "~/utils/errors";
import { DATA_TYPE_TO_TOOL_TYPE_MAP, DATA_TYPE_MAPPING } from "../../constants";
import { CrmImportJob, CrmJobConfig } from "../../hooks/types";
import { ToolConfig, ExistingFileData, FileWithType, JobConfigConstants } from "../../types";
import { DATA_TYPE_TO_FILE_KEY_MAP } from "./constants";

/**
 * Gets the display name for a data type
 * @param dataType - The data type key
 * @returns The human-readable display name for the data type
 */
export const getDataTypeName = (dataType: string): string => {
  return DATA_TYPE_MAPPING[dataType] || dataType;
};

/**
 * Checks if a job has existing uploaded files
 * @param jobDetails - The job details from the API
 * @returns True if the job has uploaded files, false otherwise
 */
export const hasExistingFiles = (jobDetails: CrmImportJob | undefined): boolean => {
  if (!jobDetails?.parsedJobConfig?.tools) {
    return false;
  }

  // Check if any tool has file-related options
  return jobDetails.parsedJobConfig.tools.some((tool) => {
    return tool.options?.some((option) => option.key.endsWith("_file"));
  });
};

/**
 * Extracts file information from job configuration tools
 * @param parsedJobConfig - The parsed job configuration
 * @returns Array of existing file data for display
 */
export const extractExistingFiles = (parsedJobConfig: CrmJobConfig): ExistingFileData[] => {
  if (!parsedJobConfig.tools) {
    return [];
  }

  return parsedJobConfig.tools
    .map((tool) => {
      const fileName = getFileNameFromTool(tool);
      const fileSize = getFileSizeFromTool(tool);
      const dataType = getDataTypeFromToolType(tool.tool_type);
      const entityType = DATA_TYPE_MAPPING[dataType] || tool.tool_type;

      // Only include tools that have file information
      if (fileName && fileName !== `${tool.tool_id || tool.tool_type || "unknown"}.csv`) {
        return {
          fileName,
          fileSize,
          dataType,
          entityType,
          toolType: tool.tool_type,
        };
      }
      return null;
    })
    .filter((file): file is ExistingFileData => file !== null);
};

/**
 * Extract file name from tool configuration
 * Reused from TestDataRelease utils with slight modifications
 */
const getFileNameFromTool = (tool: ToolConfig): string => {
  // Try to get filename from CRM import file keys
  const fileKeys = ["position_file", "contact_file", "note_file", "comm_settings_file"];
  for (const fileKey of fileKeys) {
    const fileOption = tool.options?.find((opt) => opt.key === fileKey);
    if (fileOption?.value) {
      // Extract just the filename from the full path (e.g., "gemini/accounts_base (6).csv" -> "accounts_base (6).csv")
      const fullPath = fileOption.value;
      const fileName = fullPath.includes("/") ? fullPath.split("/").pop() : fullPath;
      return fileName || fullPath;
    }
  }

  // Fallback to tool_id with .csv extension
  return `${tool.tool_id || tool.tool_type || "unknown"}.csv`;
};

/**
 * Extract file size from tool configuration
 * Reused from TestDataRelease utils
 */
const getFileSizeFromTool = (tool: ToolConfig): string => {
  // Try to get file size from tool options
  const sizeOption = tool.options?.find((opt) => opt.key === "fileSize");
  if (sizeOption?.value) {
    return sizeOption.value;
  }

  // Fallback to 0.0 KB instead of "Unknown"
  return "0.0 KB • CSV File";
};

/**
 * Get data type from tool type (reverse lookup)
 */
const getDataTypeFromToolType = (toolType: string): string => {
  // Reverse lookup in the DATA_TYPE_TO_TOOL_TYPE_MAP
  for (const [dataType, mappedToolType] of Object.entries(DATA_TYPE_TO_TOOL_TYPE_MAP)) {
    if (mappedToolType === toolType) {
      return dataType;
    }
  }

  // Fallback: try to extract from tool type name
  if (toolType.startsWith("load_")) {
    return toolType.replace("load_", "");
  }

  return toolType;
};

/**
 * Constants for job configuration
 */
export const JOB_CONFIG_CONSTANTS: JobConfigConstants = {
  JOB_TYPE: "CRM_IMPORT",
  SUBSTATE: "DATA_IMPORT",
  OPTION_KEYS: {
    ARENA: "arena",
    USER: "user",
    FILE_SIZE: "fileSize",
  },
  FILE_TYPES: {
    TSV: "TSV",
    CSV: "CSV",
  },
};

/**
 * Calculate file size display string
 */
export const calculateFileSize = (fileSizeBytes: number): string => {
  const fileSizeKB = (fileSizeBytes / 1024).toFixed(1);
  return `${fileSizeKB} KB`;
};

/**
 * Detect file type from filename
 */
export const detectFileType = (fileName: string): string => {
  return fileName.toLowerCase().endsWith(".tsv")
    ? JOB_CONFIG_CONSTANTS.FILE_TYPES.TSV
    : JOB_CONFIG_CONSTANTS.FILE_TYPES.CSV;
};

/**
 * Create file size display string with type
 */
export const createFileSizeDisplay = (fileSizeBytes: number, fileName: string): string => {
  const fileSize = calculateFileSize(fileSizeBytes);
  const fileType = detectFileType(fileName);
  return `${fileSize} • ${fileType} File`;
};

/**
 * Create tool configuration from file with type
 */
export const createToolConfig = (
  fileWithType: FileWithType,
  jobInfo: { arenaDomain: string; arenaUserEmail: string },
) => {
  const dataType = fileWithType.dataType || "";
  const toolType = DATA_TYPE_TO_TOOL_TYPE_MAP[dataType] || `load_${dataType}`;
  const fileKey = DATA_TYPE_TO_FILE_KEY_MAP[dataType] || `${dataType}_file`;
  const fileSize = createFileSizeDisplay(
    fileWithType.file.file.size,
    fileWithType.file.file.name,
  );

  const options = [
    { key: JOB_CONFIG_CONSTANTS.OPTION_KEYS.ARENA, value: jobInfo.arenaDomain },
    { key: JOB_CONFIG_CONSTANTS.OPTION_KEYS.USER, value: jobInfo.arenaUserEmail },
    {
      key: fileKey,
      value: `${jobInfo.arenaDomain}/${fileWithType.file.file.name}`,
    },
    {
      key: JOB_CONFIG_CONSTANTS.OPTION_KEYS.FILE_SIZE,
      value: fileSize,
      metadata: true,
    },
  ];

  // Add single_positions: true for load_positions_transactions tools
  if (toolType === "load_positions_transactions") {
    options.push({ key: "single_positions", value: "true" });
  }

  return {
    toolId: toolType,
    toolType: toolType,
    dataType, // Keep for sorting
    options,
  };
};

/**
 * Create and sort tools configuration from files with types
 */
export const createSortedToolsConfig = (
  filesWithTypes: FileWithType[],
  jobInfo: { arenaDomain: string; arenaUserEmail: string },
) => {
  // Import TOOL_ORDER_PRIORITY here to avoid circular dependency
  const { TOOL_ORDER_PRIORITY } = require("../../constants");

  return filesWithTypes
    .map((fileWithType) => createToolConfig(fileWithType, jobInfo))
    .sort((a, b) => {
      // Sort tools by predefined order: Accounts, Contacts, Notes, Comm Pref
      const orderA = TOOL_ORDER_PRIORITY[a.dataType] || 999;
      const orderB = TOOL_ORDER_PRIORITY[b.dataType] || 999;
      return orderA - orderB;
    })
    .map(({ dataType: _, ...tool }) => tool); // Remove dataType from final tools array
};

/**
 * Toast message types for data import workflow
 */
export enum DataImportToastType {
  NO_JOB_ID = "NO_JOB_ID",
  JOB_LOADING = "JOB_LOADING",
  JOB_NOT_LOADED = "JOB_NOT_LOADED",
  UPLOAD_SUCCESS = "UPLOAD_SUCCESS",
  UPLOAD_ERROR = "UPLOAD_ERROR",
}

/**
 * Toast messages for data import workflow
 */
const DATA_IMPORT_TOAST_MESSAGES = {
  [DataImportToastType.NO_JOB_ID]: "No job ID found. Please start from Project Setup.",
  [DataImportToastType.JOB_LOADING]:
    "Job details are still loading. Please wait and try again.",
  [DataImportToastType.JOB_NOT_LOADED]: "Job details not loaded. Please wait and try again.",
  [DataImportToastType.UPLOAD_SUCCESS]:
    "Files uploaded successfully! Validation is starting...",
  [DataImportToastType.UPLOAD_ERROR]: "Upload failed",
};

/**
 * Show toast message based on type
 */
export const showDataImportToast = (type: DataImportToastType, error?: unknown): void => {
  const message = DATA_IMPORT_TOAST_MESSAGES[type];

  switch (type) {
    case DataImportToastType.UPLOAD_SUCCESS:
      toast.success(message);
      break;
    case DataImportToastType.UPLOAD_ERROR:
      toast.error(`${message}: ${getErrorMessage(error)}`);
      break;
    default:
      toast.error(message);
      break;
  }
};
